/**
 * Escape on desktop/web leaves the snapshot (time-machine) view and returns to
 * the live game — unless a drawer, sheet or modal is open on top (those close
 * on Escape themselves, so one press shouldn't do both) or the game screen
 * isn't the focused route.
 */
export function shouldEscapeExitSnapshot(args: {
  key: string
  inSnapshot: boolean
  screenFocused: boolean
  overlayOpen: boolean
}): boolean {
  return args.key === 'Escape' && args.inSnapshot && args.screenFocused && !args.overlayOpen
}

/** The subset of a DOM element the arrow-key guard inspects. */
export interface KeyTargetLike {
  tagName?: string
  isContentEditable?: boolean
  getAttribute?: (name: string) => string | null
}

/** True when keystrokes belong to a text field (chat composer, inputs, selects). */
export function isTextEntryTarget(target: KeyTargetLike | null | undefined): boolean {
  if (!target) return false
  if (target.isContentEditable) return true
  const tag = target.tagName?.toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return target.getAttribute?.('role') === 'textbox'
}

/** The keys `snapshotArrowTarget` handles. */
export const SNAPSHOT_ARROW_KEYS: ReadonlySet<string> = new Set(['ArrowUp', 'ArrowLeft', 'ArrowDown', 'ArrowRight'])

/**
 * Desktop/web arrow keys in the snapshot view. The log lists newest first, so
 * ArrowUp (up the log) and ArrowLeft move one entry newer, ArrowDown (down the
 * log) and ArrowRight one entry older, exactly like the Newer / Older buttons
 * (`step` comes from `logStepTargets`, so it clamps at the first and newest
 * entries; the newest has no Newer, and Back to live stays explicit).
 * Returns the event index to show, or null to leave the key alone: modified
 * keys, a focused text field, an overlay on top, or another focused route.
 */
export function snapshotArrowTarget(args: {
  key: string
  modified: boolean
  enabled: boolean
  textEntryFocused: boolean
  step: { previous: number | null; next: number | null }
}): number | null {
  if (!args.enabled || args.modified || args.textEntryFocused) return null
  if (args.key === 'ArrowUp' || args.key === 'ArrowLeft') return args.step.next
  if (args.key === 'ArrowDown' || args.key === 'ArrowRight') return args.step.previous
  return null
}
